import keys from '@debbie/common/dist/cache/keys.js'
import { useMutation } from '@debbie/common/dist/util/hooks.js'
import * as satisfactionsAPI from '@debbie/cortex/dist/api/satisfactions.js'
import {
  casesEntity,
  ensureCaseLoaded,
  useCase,
} from '@debbie/cortex/dist/cases/index.js'
import { h } from '@debbie/cortex/dist/index.js'
import q from '@debbie/cortex/dist/query.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import {
  satisfactionsEntity,
  useSatisfactions,
} from '@debbie/cortex/dist/satisfactions/index.js'
import { Container } from '@debbie/iris/dist/components/Grid/index.js'
import ArrowLeft from '@debbie/iris/dist/components/Icons/ArrowLeft.js'
import Frown from '@debbie/iris/dist/components/Icons/Frown.js'
import Meh from '@debbie/iris/dist/components/Icons/Meh.js'
import Smile from '@debbie/iris/dist/components/Icons/Smile.js'
import ThumbsUp from '@debbie/iris/dist/components/Icons/ThumbsUp.js'
import {
  Button,
  Content,
  PageBase,
  Panel,
  PlaceholderLoader,
  Textarea,
} from '@debbie/iris/dist/components/index.js'
import { errorNotification } from '@debbie/iris/dist/utils/errors.js'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { styled } from 'styled-components'

type SatisfactionNumber = 1 | 2 | 3 | 4 | 5

const getSatisfaction = (
  preSelectedSatisfaction: string | undefined,
): null | SatisfactionNumber => {
  if (!preSelectedSatisfaction) {
    return null
  }

  const parsedSatisfaction = parseInt(preSelectedSatisfaction)

  return parsedSatisfaction >= 0 && parsedSatisfaction <= 5
    ? (parsedSatisfaction as SatisfactionNumber)
    : null
}

const Satisfaction = () => {
  const params: { id: string; preSelectedSatisfaction: string } = useParams()
  const { id, preSelectedSatisfaction } = params

  const caseIdLoader = useCase(id)
  const caseId = h.hasData(caseIdLoader) ? caseIdLoader.data.caseId : null
  const singleCaseLoader = useCase(caseId)
  const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null

  const satisfactionsLoadable = useSatisfactions(singleCase?.caseId)
  const satisfactions = h.hasData(satisfactionsLoadable)
    ? satisfactionsLoadable.data
    : null
  const lang = useSelector((s: RootState) => s.lang)

  const [comment, setComment] = useState<null | string>(null)
  const [satisfaction, setSatisfaction] = useState<null | SatisfactionNumber>(
    getSatisfaction(preSelectedSatisfaction),
  )

  const sendSatisfactionMutation = useMutation(
    async () => {
      if (!singleCase) {
        return
      }
      await satisfactionsAPI.add(
        singleCase.caseId,
        satisfaction!,
        comment ?? '',
      )
      q.invalidate(keys.write.modify(casesEntity, singleCase.caseId))
      q.invalidate(keys.write.create(satisfactionsEntity))
      await ensureCaseLoaded(singleCase.caseId)
    },
    {
      onError: (err) => {
        errorNotification(lang, err, 'create', lang.errorEntities.satisfaction)
      },
    },
  )

  return (
    <div>
      <Content fullHeight>
        <PageBase>
          <Container style={{ maxWidth: '500px', textAlign: 'center' }}>
            {singleCase && (
              // @ts-ignore
              <Link
                to={`/cases/${singleCase.id}`}
                style={{
                  textDecoration: 'none',
                }}
              >
                <p
                  style={{
                    color: 'grey',
                    fontSize: '.8em',
                    textAlign: 'left',
                  }}
                >
                  <b>
                    <ArrowLeft style={{ margin: '-.2em 0px' }} />{' '}
                    {lang.satisfaction.backToMyCase}
                  </b>
                </p>
              </Link>
            )}
            <Panel>
              {satisfactions ? (
                satisfactions.length === 0 ? (
                  <>
                    <h1>{lang.satisfaction.header}</h1>
                    <p>{lang.satisfaction.intro}</p>
                    <h2>
                      {lang.satisfaction.whichSmiley}
                      {singleCase && singleCase.id}
                    </h2>
                    <SmileySelect>
                      <SmileySelectItem
                        selected={satisfaction === 1}
                        onClick={() => setSatisfaction(1)}
                      >
                        <Frown />
                      </SmileySelectItem>
                      <SmileySelectItem
                        selected={satisfaction === 3}
                        onClick={() => setSatisfaction(3)}
                      >
                        <Meh />
                      </SmileySelectItem>
                      <SmileySelectItem
                        selected={satisfaction === 5}
                        onClick={() => setSatisfaction(5)}
                      >
                        <Smile />
                      </SmileySelectItem>
                    </SmileySelect>
                    <h2>{lang.satisfaction.optionalComment}</h2>
                    <Textarea
                      placeholder={lang.satisfaction.optionalComment}
                      onChange={(e) => setComment(e.target.value)}
                    />
                    <Button
                      block
                      disabled={!satisfaction || !singleCase}
                      loading={sendSatisfactionMutation.isLoading}
                      onClick={() => sendSatisfactionMutation.mutate()}
                    >
                      {lang.satisfaction.submit}
                    </Button>
                  </>
                ) : (
                  <>
                    <p
                      style={{
                        fontSize: '5em',
                      }}
                    >
                      <ThumbsUp />
                    </p>
                    <h1>{lang.satisfaction.thanks}</h1>
                  </>
                )
              ) : (
                <PlaceholderLoader />
              )}
            </Panel>
          </Container>
        </PageBase>
      </Content>
    </div>
  )
}

export default Satisfaction

const SmileySelect = styled.div`
  font-size: 4em;
`

const SmileySelectItem = styled.div<{ selected?: boolean }>`
  cursor: pointer;
  margin: 0 5px;
  padding: 15px 10px 0px 10px;
  border-radius: 4px;
  display: inline-block;

  background-color: ${(props) =>
    props.selected ? 'lightgrey' : 'transparent'};
`
